import { Article } from '@/modules/base/components/inputs/article-select-input/article-select-input'
import { PriceKind } from '@/modules/transactionV2/models/TransactionBase'
import { PositionTypes } from './PositionTypes'
import { uniqueId } from 'lodash'
import { formatDate } from '@/modules/_helpers/formatDate'
import lang from '@/lang'
import { TransactionBaseInterface, TotalTransactionInterface } from './TransactionBase'

import { DEFAULT_UNIT, transactionTypes } from '@/modules/base/util/constants'
import { user } from 'ampersand-app'
import positionCalculation from '@/modules/transactionV2/util/position_calculation'
import { getMatchingGraduatedPriceEntry } from '@/modules/transactionV2/components/positions/helpers/getMatchingGraduatedPriceEntry'
import { getAppState } from '@/redux/store'

const {
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_DEPOSIT_INVOICE,
  TRANSACTION_TYPE_CLOSING_INVOICE,
  TRANSACTION_TYPE_DELIVERY_NOTE,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
  TRANSACTION_TYPE_OFFER,
  TRANSACTION_TYPE_PURCHASE,
  TRANSACTION_TYPE_PARTIAL_INVOICE,
} = transactionTypes

export interface Stock {
  stockAmount: number
  reservedAmount: number
  availableAmount: number
}

export type RelatedTransactionPosition = {
  id: string
  number: string
  date: string
  title: string
  description: string
  amount: number
  totalNet: number
  totalGross: number
  priceNet: number
  metaData: {
    type: PositionTypes
  }
}

export type RelatedTransaction = {
  id: number
  positions: RelatedTransactionPosition[]
  number: string
  date: string
  totalNet: string
  totalGross: string
}

export interface IPositionItemMetaData {
  isRelatedTransactionPosition: boolean
  relatedTransaction?: RelatedTransaction
  id: number
  type: PositionTypes
  number: number
  allowMove: boolean
  invoiceNo?: string
  isAlternative?: boolean
  isOptional?: boolean
  isSelectedByCustomer?: boolean
}

export interface PriceList {
  quantity: number
  netUnitPrice: number
  grossUnitPrice: number
  laborShare?: number
  laborShareGross?: number
  id: number
}

export interface IPositionItem {
  _rowId: string
  id: string
  title: string
  description: string
  showDescription: boolean
  amount: number
  unit: string
  price: number
  priceGross: number
  purchasePrice: number
  purchasePriceGross: number
  laborShare: number
  laborShareGross: number

  selected?: boolean

  weight: number
  stock: Stock
  isStockManaged: boolean
  isGraduatedPriceManaged: boolean
  graduatedPriceList: PriceList[]
  defaultNetPrice: number
  defaultGrossPrice: number
  isRowPriceUpdated: boolean
  isDifferentialTax: boolean
  differentialTaxTexts: object
  minimumStockAmount: number
  metaData?: IPositionItemMetaData
  orderPositionId?: string
  bookkeepingAccountId?: string
  notesAlert: boolean
  notes: string
  createOrUpdateAsset: boolean

  allowMove: boolean
  allowEdit: boolean
}

export class PositionItem implements IPositionItem {
  _rowId: string
  id: string
  title: string
  description: string
  showDescription: boolean
  amount: number
  unit: string

  weight: number
  stock: Stock
  isStockManaged: boolean
  isDifferentialTax: boolean
  differentialTaxTexts: object
  minimumStockAmount: number
  metaData: IPositionItemMetaData
  orderPositionId?: string
  bookkeepingAccountId?: string
  notesAlert: boolean
  notes: string
  createOrUpdateAsset: boolean

  allowMove: boolean
  allowEdit: boolean

  price: number
  priceGross: number
  purchasePrice: number
  purchasePriceGross: number
  laborShare: number
  laborShareGross: number
  isGraduatedPriceManaged: boolean
  graduatedPriceList: PriceList[]
  defaultNetPrice: number
  defaultGrossPrice: number
  isRowPriceUpdated: boolean

  constructor(options?: IPositionItem, transactionBase?: TransactionBaseInterface) {
    this.assignValues(options, transactionBase)
  }

  public assignValues(options: IPositionItem, transactionBase?: TransactionBaseInterface) {
    this.showDescription = true
    this.amount = 1
    this.unit = ''
    this.title = ''
    this.description = ''

    this.allowMove = true
    this.allowEdit = true

    if (options?.metaData?.type) {
      if (options.metaData.type === PositionTypes.HORIZONTAL_RULE) {
        this.description = '<div><hr /></div>'
      }
      if (options.metaData.type === PositionTypes.PAGE_BREAK) {
        this.description = '<div style="page-break-after: always;"></div>'
      }
      if (options.metaData.type === PositionTypes.HEADLINE && options.metaData.isRelatedTransactionPosition && options.metaData.relatedTransaction) {
        const { number, date } = options.metaData.relatedTransaction
        this.description = `<div><span style="font-size:16px;"><strong>${lang.transactionEdit.relatedTransactions.additionalDescription(number, formatDate(date))}</strong></span></div>`
      }
      if (options.metaData.type === PositionTypes.SUBTOTALS) {
        this.description = `<div><span><strong>${lang.transactionEdit.positions.types[PositionTypes.SUBTOTALS].label}</strong></span></div>`
      }
    } else {
      if (!this.metaData) {
        this.metaData = {} as IPositionItemMetaData
      }
      this.metaData.type = PositionTypes.CUSTOM
    }

    Object.assign(this, options)

    if (options?._rowId === undefined) {
      this._rowId = uniqueId('new-')
    }

    if (this.metaData?.allowMove !== undefined) {
      this.allowMove = this.metaData.allowMove
    }

    const hasRelatedTransactions = transactionBase?.relatedTransactions?.length > 0

    switch (transactionBase?.type) {
      case TRANSACTION_TYPE_DEPOSIT_INVOICE:
        break

      case TRANSACTION_TYPE_INVOICE:
      case TRANSACTION_TYPE_PARTIAL_INVOICE:
        if (this.orderPositionId && !this.metaData.isRelatedTransactionPosition) {
          this.allowMove = false
          this.allowEdit = false
        }
        if (this.metaData.isRelatedTransactionPosition && !this.metaData.relatedTransaction) {
          this.allowMove = false
        }
        break

      case TRANSACTION_TYPE_DELIVERY_NOTE:
        if (hasRelatedTransactions) {
          this.allowEdit = false
        }
        break

      case TRANSACTION_TYPE_OFFER:
        if (this.metaData.isAlternative) {
          this.allowMove = false
        }
        break

      case TRANSACTION_TYPE_CLOSING_INVOICE:
        if (hasRelatedTransactions && Boolean(this.metaData?.invoiceNo)) {
          this.allowEdit = false
        }
        break
      case TRANSACTION_TYPE_MANUAL_CREDIT_NOTE:
        this.amount = Math.abs(this.amount)
        break
    }
    return this
  }

  public convertArticleToPosition(article: Article, transactionBase?: TransactionBaseInterface): PositionItem {
    const newPosition = {} as IPositionItem

    newPosition.title = article.title
    newPosition.description = article.description
    newPosition.unit = article.unit

    let metaData

    // if article.id is empty, it's always a new article created by user input
    const positionType = (article.id) ? PositionTypes.ARTICLE : PositionTypes.CUSTOM

    switch (positionType) {
      case PositionTypes.ARTICLE:
        metaData = {
          id: article.id,
          type: PositionTypes.ARTICLE,
          number: article.number,
          purchasePrice: this instanceof PositionPriceItem ? article.purchasePrice : undefined,
          calculationBase: this instanceof PositionPriceItem ? article.calculationBase : undefined,
        }
        break
      case PositionTypes.CUSTOM:
        metaData = {
          type: PositionTypes.CUSTOM,
        }
    }

    if (article.isAlternative) {
      metaData.isAlternative = article.isAlternative
      metaData.allowMove = false
    }
    if (article.isOptional) metaData.isOptional = article.isOptional

    newPosition.description = article.description
    newPosition.weight = article.weight
    if (!newPosition.unit) {
      // set unit of complete new positions to default unit
      newPosition.unit = DEFAULT_UNIT
    }
    newPosition.metaData = metaData
    newPosition.stock = article.stock
    newPosition.isStockManaged = article.isStockManaged
    newPosition.notesAlert = article.notesAlert
    newPosition.notes = article.notes
    newPosition.isGraduatedPriceManaged = article.isGraduatedPriceManaged
    newPosition.graduatedPriceList = (article.graduatedPriceList || []) as unknown as PriceList[]
    newPosition.isRowPriceUpdated = false
    newPosition.isDifferentialTax = article.isDifferentialTax
    newPosition.differentialTaxTexts = article.differentialTaxTexts
    newPosition.minimumStockAmount = article.minimumStockAmount
    newPosition.bookkeepingAccountId = article.bookkeepingAccountId

    const result = Object.assign(
      Object.create(
        Object.getPrototypeOf(this),
      ),
    )
    return result.assignValues(newPosition, transactionBase)
  }
}

export interface IPositionItemPriceMetaData {
  isRelatedTransactionPosition: boolean
  relatedTransaction: RelatedTransaction
  id: number,
  type: PositionTypes,
  number: number,
  calculationBase: PriceKind,
  allowMove: boolean,
  invoiceNo?: string,
  isAlternative?: boolean
  isOptional?: boolean
  isSelectedByCustomer?: boolean
}

export interface IPositionPriceItem extends IPositionItem {
  priceKind: PriceKind
  discountPercent: number
  vatPercent: number
  purchasePrice: number,
  purchasePriceGross: number,
  calculationBase: PriceKind,

  priceGross: number
  priceGrossAfterDiscount: number
  priceNet: number
  priceNetAfterDiscount: number
  totalNet: number
  totalNetAfterDiscount: number
  totalGross: number
  totalGrossAfterDiscount: number
  defaultNetPrice: number
  defaultGrossPrice: number
  isRowPriceUpdated: boolean

  // Labor share fields (calculated by backend)
  laborShareNet?: number
  laborShareNetAfterDiscount?: number
  laborShareGrossAfterDiscount?: number
  totalLaborShareNet?: number
  totalLaborShareGross?: number
  totalLaborShareNetAfterDiscount?: number
  totalLaborShareGrossAfterDiscount?: number
  laborShareVat?: number

  metaData: IPositionItemPriceMetaData
}

export class PositionPriceItem extends PositionItem implements IPositionPriceItem {
  // eslint-disable-next-line no-useless-constructor
  constructor(options?: IPositionPriceItem, transactionBase?: TotalTransactionInterface) {
    super(options, transactionBase)
  }

  priceKind: PriceKind
  discountPercent: number
  vatPercent: number
  purchasePrice: number
  purchasePriceGross: number
  calculationBase: PriceKind

  priceGross: number
  priceGrossAfterDiscount: number
  priceNet: number
  priceNetAfterDiscount: number
  totalNet: number
  totalNetAfterDiscount: number
  totalGross: number
  totalGrossAfterDiscount: number
  defaultNetPrice: number
  defaultGrossPrice: number
  isRowPriceUpdated: boolean

  laborShare: number
  laborShareGross: number
  laborShareNet: number
  laborShareNetAfterDiscount: number
  laborShareGrossAfterDiscount: number
  totalLaborShareNet: number
  totalLaborShareGross: number
  totalLaborShareNetAfterDiscount: number
  totalLaborShareGrossAfterDiscount: number
  laborShareVat: number

  metaData: IPositionItemPriceMetaData

  calculate(transactionType?: string) {
    // For purchase transactions, use purchase prices instead of selling prices
    const isPurchaseTransaction = transactionType === TRANSACTION_TYPE_PURCHASE

    if (isPurchaseTransaction) {
      // Create a temporary position object with purchase prices for calculation
      const tempPosition = {
        ...this,
        price: this.priceKind === PriceKind.Gross ? this.purchasePriceGross : this.purchasePrice,
      }

      const calculatedValues = this.priceKind === PriceKind.Gross
        ? positionCalculation.calculatePricesBasedOnPriceGross(tempPosition)
        : positionCalculation.calculatePricesBasedOnPriceNet(tempPosition)

      Object.assign(this, calculatedValues)
    } else {
      // Use normal selling prices for all other transaction types
      const calculatedValues = this.priceKind === PriceKind.Gross
        ? positionCalculation.calculatePricesBasedOnPriceGross(this)
        : positionCalculation.calculatePricesBasedOnPriceNet(this)

      Object.assign(this, calculatedValues)
    }
  }

  public assignValues(options: IPositionPriceItem, transactionBase?: TotalTransactionInterface) {
    this.metaData = {} as IPositionItemPriceMetaData
    super.assignValues(options, transactionBase)
    this.priceGross = this.priceGross || 0
    this.priceNet = this.priceNet || 0
    this.laborShare = this.laborShare ?? this.laborShareNet ?? 0
    this.laborShareGross = this.laborShareGross || 0
    this.defaultNetPrice = this.defaultNetPrice || this.priceNet || 0
    this.defaultGrossPrice = this.defaultGrossPrice || this.priceGross
    this.isRowPriceUpdated = this.isRowPriceUpdated || false
    this.discountPercent = this.discountPercent || 0

    // Check if company is VAT-exempt (has defaultRevenueAccount)
    const companyData = getAppState()?.settings?.company
    const isVatExempt = Boolean(companyData?.defaultRevenueAccount)

    this.vatPercent = this.vatPercent ?? (isVatExempt || transactionBase?.customerData?.netPriceAsDefault ? 0 : 19) // TODO get current VAT by Date
    this.priceKind = transactionBase?.priceKind || options?.priceKind || PriceKind.Net
    this.isDifferentialTax = this.isDifferentialTax || false
    this.graduatedPriceList = this.graduatedPriceList || []
    this.isGraduatedPriceManaged = Boolean(this.graduatedPriceList.length)

    // Sales graduated prices apply to sales docs only — not purchase orders (supplier EK / Staffelpreise)
    const isPurchaseTransaction = transactionBase?.type === TRANSACTION_TYPE_PURCHASE
    if (!isPurchaseTransaction && this.isGraduatedPriceManaged && !this.isRowPriceUpdated) {
      const matchingEntry = getMatchingGraduatedPriceEntry(this.graduatedPriceList, this.amount)
      if (matchingEntry) {
        this.priceGross = matchingEntry.grossUnitPrice ?? 0
        this.priceNet = matchingEntry.netUnitPrice ?? 0
        this.laborShare = matchingEntry.laborShare ?? 0
        this.laborShareGross = matchingEntry.laborShareGross ?? 0
      }
    }

    if (user.isSmallBusiness) {
      this.vatPercent = 0
      this.priceKind = PriceKind.Net
    }

    this.calculate(transactionBase?.type)
    return this
  }

  public convertArticleToPosition(article: Article, transactionBase?: TotalTransactionInterface) {
    const newPosition = super.convertArticleToPosition(article) as unknown as IPositionPriceItem
    newPosition.vatPercent = article.vatPercent

    // For purchase transactions, use purchase prices as the base prices
    const isPurchaseTransaction = transactionBase?.type === TRANSACTION_TYPE_PURCHASE

    if (isPurchaseTransaction) {
      newPosition.priceNet = article.purchasePrice
      newPosition.priceGross = article.purchasePriceGross
      newPosition.defaultNetPrice = article.purchasePrice
      newPosition.defaultGrossPrice = article.purchasePriceGross
    } else {
      newPosition.priceNet = article.price
      newPosition.priceGross = article.priceGross
      newPosition.defaultNetPrice = article.price
      newPosition.defaultGrossPrice = article.priceGross
    }

    newPosition.purchasePrice = article.purchasePrice
    newPosition.purchasePriceGross = article.purchasePriceGross
    newPosition.laborShare = article.laborShare
    newPosition.laborShareGross = article.laborShareGross
    newPosition.priceKind = this.priceKind || PriceKind.Net
    newPosition.discountPercent = this.discountPercent

    return new PositionPriceItem(newPosition, transactionBase)
  }
}
